import { API_ENDPOINTS } from '@/constants/api';
import { api } from '@/shared/api';
import { BaseApi } from '@/shared/api/baseApi';
import type { PaginatedResponse } from '@/shared/types/entity';
import type { PermissionOption, Role, RoleData, RoleOption } from '../types';

const base = new BaseApi<Role, RoleData>(API_ENDPOINTS.ROLES);

export const rolesApi = {
  getList: async (params?: { perPage?: number; page?: number; search?: string }) => {
    const response = await api.get<
      | PaginatedResponse<Role>
      | {
          data: Role[];
          meta: { current_page: number; per_page: number; total: number; last_page: number };
        }
    >(API_ENDPOINTS.ROLES.LIST, { params });
    if (!('meta' in response.data)) return response.data;
    return {
      data: response.data.data,
      currentPage: response.data.meta.current_page,
      perPage: response.data.meta.per_page,
      total: response.data.meta.total,
      lastPage: response.data.meta.last_page,
    };
  },
  getOne: async (id: number): Promise<Role> => {
    const response = await api.get<Role | { data: Role }>(API_ENDPOINTS.ROLES.SHOW(String(id)));
    return 'data' in response.data ? response.data.data : response.data;
  },
  create: async (data: RoleData): Promise<Role> => {
    const response = await api.post<Role | { data: Role }>(API_ENDPOINTS.ROLES.CREATE, data);
    return 'data' in response.data ? response.data.data : response.data;
  },
  update: async (id: number, data: Partial<RoleData>): Promise<Role> => {
    const response = await api.put<Role | { data: Role }>(
      API_ENDPOINTS.ROLES.UPDATE(String(id)),
      data,
    );
    return 'data' in response.data ? response.data.data : response.data;
  },
  delete: base.delete.bind(base),
  getPermissions: async (): Promise<PermissionOption[]> => {
    const response = await api.get<PermissionOption[] | { data: PermissionOption[] }>(
      API_ENDPOINTS.ROLES.PERMISSIONS,
    );
    return Array.isArray(response.data) ? response.data : response.data.data;
  },
  getOptions: async (): Promise<RoleOption[]> => {
    const response = await api.get<{ roles: RoleOption[] }>(API_ENDPOINTS.ROLES.OPTIONS);
    return response.data.roles;
  },
};
